import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAuth0 } from '@auth0/auth0-react';
import { Box, Typography } from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';

import { FBoxContainer, FHButton, FHConfirmDialog } from '../../../../base';
import { FORTA_BLUE } from '../../../../../utils/colors';
import { boxContainerHeaderStyle, boxContainerStyle, contentStyle } from '../constants';
import GoalRow from './GoalRow';
import TargetSelectModalContent from './TargetSelectModalContent';
import { parentSupervisionActions } from '../../../../../store';
import { useParentSupervisionEditable } from '../../../../../hooks';
import { getTargetIDsOfAllGoals } from './utils';

import type { RootState } from '../../../../../store';
import type {
  IParentSupervision,
  ITargetTrialDurationEvent,
  ITreatmentPlanGoal,
  TargetProgress,
} from '../../../../../utils/types';

export interface ParentGoalTarget {
  id?: number;
  target_id: number;
  target_label: string;
  successful_attempts: number;
  occurrence: number;
  total_attempts: number;
  unsuccessful_attempts: number;
  rate_events: any[];
  duration_events: ITargetTrialDurationEvent[];
}

interface ParentTargetProgressProps {
  isSupervisionCompleted: boolean;
  parentSNLoading: boolean;
  loadingTargetProgress: boolean;
  parentSupervision: IParentSupervision | null;
}

function ParentTargetProgress({
  isSupervisionCompleted,
  parentSNLoading,
  loadingTargetProgress,
  parentSupervision,
}: ParentTargetProgressProps) {
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();

  const { caregiverGoals = [] } = useSelector((state: RootState) => state.goals);

  const [openTargetSelectModal, setOpenTargetSelectModal] = useState<boolean>(false);
  const [selectedTargetIDs, setSelectedTargetIDs] = useState<number[]>([]);
  const [targetProgress, setTargetProgress] = useState<TargetProgress[]>([]);
  const [dataLoading, setDataLoading] = useState<boolean>(false);

  const activeCaregiverGoals = caregiverGoals.filter((goal: ITreatmentPlanGoal) => goal?.is_active);

  const isEditable =
    useParentSupervisionEditable() && !loadingTargetProgress && !parentSNLoading && !dataLoading;
  const parentSupervisionId = parentSupervision?.id;

  const areAllTargetsSelected = useMemo(() => {
    const allTargetCount = activeCaregiverGoals.reduce(
      (ids: number[], goal) => [
        ...ids,
        ...goal.targets.flatMap((target) =>
          target.is_active && target.id !== undefined ? [target.id] : [],
        ),
      ],
      [],
    ).length;

    const savedTargetCount = getTargetIDsOfAllGoals(targetProgress).length;
    return allTargetCount === savedTargetCount;
  }, [activeCaregiverGoals, targetProgress]);

  useEffect(() => {
    if (!parentSupervision?.target_progress) {
      setTargetProgress([]);
      setSelectedTargetIDs([]);
      return;
    }
    setTargetProgress(parentSupervision?.target_progress);
  }, [parentSupervision?.target_progress]);

  const handleAddTargets = async () => {
    if (parentSupervisionId === undefined) return;

    setDataLoading(true);
    const addedTargetIDs = getTargetIDsOfAllGoals(targetProgress);
    const filteredTargetIDsToBeAdded = selectedTargetIDs.filter(
      (targetId) => !addedTargetIDs.includes(targetId),
    );
    await Promise.all(
      filteredTargetIDsToBeAdded.map((target_id) => {
        const data = {
          evaluation: null,
          target_id,
        };
        return dispatch(
          parentSupervisionActions.createTargetEvaluation({
            getAccessTokenSilently,
            parentSupervisionId,
            data,
          }),
        );
      }),
    );
    await dispatch(
      parentSupervisionActions.getParentSupervision({
        parentSupervisionId,
        getAccessTokenSilently,
      }),
    );
    setDataLoading(false);
    setOpenTargetSelectModal(false);
    setSelectedTargetIDs([]);
  };

  return (
    <FBoxContainer
      sx={boxContainerStyle}
      headerStyle={boxContainerHeaderStyle}
      contentStyle={contentStyle}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 4 }}>
        <Typography color={FORTA_BLUE.primary} fontSize={21}>
          Goals and target progress
        </Typography>
        {!isSupervisionCompleted && (
          <FHButton
            disabled={!isEditable || areAllTargetsSelected}
            sx={{ height: 40, mr: 3 }}
            startIcon={<AddCircleOutlineIcon />}
            onClick={() => setOpenTargetSelectModal(true)}
            data-testid="addGoalButtonParentGoal"
          >
            GOAL & TARGET
          </FHButton>
        )}
      </Box>
      {targetProgress.length > 0 ? null : (
        <Typography color={FORTA_BLUE.primary} mb={2}>
          Please add at least one target.
        </Typography>
      )}
      {parentSupervisionId !== undefined && targetProgress.map((goal) => (
        <GoalRow
          key={goal.goal_label}
          goalName={goal.goal_label}
          dataCollectionMethod={goal.data_collection_method}
          targets={goal.target_evaluations}
          isSupervisionCompleted={isSupervisionCompleted}
          parentSupervisionId={parentSupervisionId}
          isEditable={isEditable}
          dataLoading={loadingTargetProgress || parentSNLoading || dataLoading}
        />
      ))}

      <FHConfirmDialog
        open={openTargetSelectModal}
        title={'Add goals and targets'}
        contentRenderer={() => (
          <Box sx={{ p: 0.5, mb: 1.5 }}>
            <TargetSelectModalContent
              isEditable={isEditable}
              activeCaregiverGoals={activeCaregiverGoals}
              selectedTargetIDs={selectedTargetIDs}
              onUpdate={(targetIDs: number[]) => setSelectedTargetIDs(targetIDs)}
              targetProgress={targetProgress ?? []}
            />
          </Box>
        )}
        titleStyles={{ color: FORTA_BLUE.primary }}
        dialogStyles={{ backgroundColor: FORTA_BLUE.light3 }}
        disableSubmitButton={!isEditable}
        disableCancelButton={!isEditable}
        submitLoading={!isEditable}
        dialogHeight={'auto'}
        submittext={'Add'}
        submitBtnTestId={'AddButton'}
        canceltext={'Cancel'}
        onClose={() => {
          if (!isEditable) return;
          setOpenTargetSelectModal(false);
          setSelectedTargetIDs([]);
        }}
        onSubmit={() => {
          handleAddTargets();
        }}
      />
    </FBoxContainer>
  );
}

export default ParentTargetProgress;
